import React, { useState, useEffect, useCallback, Fragment } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import CustomLoader from '../../../components/CustomLoader';
import './scheduledMail.scss';

const API = process.env.REACT_APP_API_BASE_URL;

const DAYS   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const FREQS  = ['hourly','daily','weekly','monthly','yearly'];
const STATUS_COLORS = { active: '#16a34a', paused: '#d97706', stopped: '#dc2626' };

const emptyForm = {
    title: '', subject: '', body: '',
    frequency: 'daily', hour: 8,
    dayOfWeek: 1, dayOfMonth: 1,
    recipientType: 'group',
    mailGroupId: '', emails: '',
};

function fmt(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

const ScheduleFormModal = ({ title, form, onChange, onSubmit, onClose, groups }) => {
    const f = form.frequency;
    const isGroup = form.recipientType === 'group';
    return (
        <div className="sm-modal__backdrop" onClick={onClose}>
            <div className="sm-modal" onClick={e => e.stopPropagation()}>
                <div className="sm-modal__header">
                    <h3 className="sm-modal__title">{title}</h3>
                    <button className="sm-modal__close" onClick={onClose}>✕</button>
                </div>
                <div className="sm-modal__body">
                    {/* Title */}
                    <div className="sm-modal__field">
                        <label className="sm-modal__label">Internal Title</label>
                        <input className="sm-modal__input" value={form.title} onChange={e => onChange('title', e.target.value)} placeholder="e.g. Weekly Newsletter" />
                    </div>
                    {/* Subject */}
                    <div className="sm-modal__field">
                        <label className="sm-modal__label">Email Subject</label>
                        <input className="sm-modal__input" value={form.subject} onChange={e => onChange('subject', e.target.value)} placeholder="Email subject..." />
                    </div>
                    {/* Body */}
                    <div className="sm-modal__field">
                        <label className="sm-modal__label">Email Body</label>
                        <div className="sm-modal__editor">
                            <ReactQuill theme="snow" value={form.body} onChange={v => onChange('body', v)} />
                        </div>
                    </div>
                    {/* Frequency row */}
                    <div className="sm-modal__row">
                        <div className="sm-modal__field">
                            <label className="sm-modal__label">Frequency</label>
                            <select className="sm-modal__input" value={form.frequency} onChange={e => onChange('frequency', e.target.value)}>
                                {FREQS.map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase()+f.slice(1)}</option>)}
                            </select>
                        </div>
                        {f !== 'hourly' && (
                            <div className="sm-modal__field">
                                <label className="sm-modal__label">Hour (0–23) IST</label>
                                <input className="sm-modal__input" type="number" min={0} max={23} value={form.hour} onChange={e => onChange('hour', +e.target.value)} />
                            </div>
                        )}
                        {f === 'weekly' && (
                            <div className="sm-modal__field">
                                <label className="sm-modal__label">Day of Week</label>
                                <select className="sm-modal__input" value={form.dayOfWeek} onChange={e => onChange('dayOfWeek', +e.target.value)}>
                                    {DAYS.map((d,i) => <option key={i} value={i}>{d}</option>)}
                                </select>
                            </div>
                        )}
                        {f === 'monthly' && (
                            <div className="sm-modal__field">
                                <label className="sm-modal__label">Day of Month (1–31)</label>
                                <input className="sm-modal__input" type="number" min={1} max={31} value={form.dayOfMonth} onChange={e => onChange('dayOfMonth', +e.target.value)} />
                            </div>
                        )}
                    </div>
                    {/* Recipient type */}
                    <div className="sm-modal__field">
                        <label className="sm-modal__label">Recipient Type</label>
                        <div className="sm-modal__radios">
                            {['group','individual'].map(rt => (
                                <label key={rt} className="sm-modal__radio">
                                    <input type="radio" value={rt} checked={form.recipientType === rt} onChange={() => onChange('recipientType', rt)} />
                                    <span>{rt === 'group' ? '👥 Mail Group' : '✉️ Individual Emails'}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    {isGroup ? (
                        <div className="sm-modal__field">
                            <label className="sm-modal__label">Mail Group</label>
                            <select className="sm-modal__input" value={form.mailGroupId} onChange={e => onChange('mailGroupId', e.target.value)}>
                                <option value="">— Select a group —</option>
                                {groups.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                            </select>
                        </div>
                    ) : (
                        <div className="sm-modal__field">
                            <label className="sm-modal__label">Email Addresses (comma-separated)</label>
                            <textarea className="sm-modal__input sm-modal__input--ta" rows={3} value={form.emails} onChange={e => onChange('emails', e.target.value)} placeholder="alice@example.com, bob@example.com" />
                        </div>
                    )}
                </div>
                <div className="sm-modal__footer">
                    <button className="sm-modal__btn sm-modal__btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="sm-modal__btn sm-modal__btn--save" onClick={onSubmit}>Save Schedule</button>
                </div>
            </div>
        </div>
    );
};

const ScheduledMail = () => {
    const [schedules, setSchedules] = useState([]);
    const [groups, setGroups]       = useState([]);
    const [loading, setLoading]     = useState(false);
    const [toast, setToast]         = useState(null);
    const [showForm, setShowForm]   = useState(false);
    const [editItem, setEditItem]   = useState(null);
    const [form, setForm]           = useState(emptyForm);
    const [confirmDel, setConfirmDel] = useState(null);

    const showToast = (msg, ok = true) => {
        setToast({ msg, ok });
        setTimeout(() => setToast(null), 4000);
    };

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [sRes, gRes] = await Promise.all([
                axios.get(`${API}/scheduled-mail`),
                axios.get(`${API}/mail/groups`),
            ]);
            setSchedules(sRes.data);
            setGroups(gRes.data);
        } catch { showToast('Failed to load schedules', false); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setForm(emptyForm); setEditItem(null); setShowForm(true); };
    const openEdit   = (s) => {
        setForm({
            title: s.title, subject: s.subject, body: s.body,
            frequency: s.frequency, hour: s.hour ?? 8,
            dayOfWeek: s.dayOfWeek ?? 1, dayOfMonth: s.dayOfMonth ?? 1,
            recipientType: s.recipientType,
            mailGroupId: s.mailGroupId?._id || s.mailGroupId || '',
            emails: (s.emails || []).join(', '),
        });
        setEditItem(s);
        setShowForm(true);
    };

    const handleChange = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const handleSubmit = async () => {
        const payload = {
            title: form.title, subject: form.subject, body: form.body,
            frequency: form.frequency, recipientType: form.recipientType,
        };
        if (form.frequency !== 'hourly') payload.hour = form.hour;
        if (form.frequency === 'weekly')  payload.dayOfWeek   = form.dayOfWeek;
        if (form.frequency === 'monthly') payload.dayOfMonth  = form.dayOfMonth;
        if (form.recipientType === 'group') {
            payload.mailGroupId = form.mailGroupId;
        } else {
            payload.emails = form.emails.split(',').map(e => e.trim()).filter(Boolean);
        }
        try {
            setLoading(true);
            if (editItem) {
                await axios.put(`${API}/scheduled-mail/${editItem._id}`, payload);
                showToast('Schedule updated!');
            } else {
                await axios.post(`${API}/scheduled-mail`, payload);
                showToast('Schedule created!');
            }
            setShowForm(false);
            load();
        } catch { showToast('Failed to save schedule', false); setLoading(false); }
    };

    const handleStatus = async (id, status) => {
        try {
            setLoading(true);
            await axios.patch(`${API}/scheduled-mail/${id}/status`, { status });
            showToast(`Schedule ${status}`);
            load();
        } catch { showToast('Failed to update status', false); setLoading(false); }
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await axios.delete(`${API}/scheduled-mail/${id}`);
            setConfirmDel(null);
            showToast('Schedule deleted');
            load();
        } catch { showToast('Failed to delete', false); setLoading(false); }
    };

    const describeSchedule = (s) => {
        const h = `${s.hour ?? 8}:00 IST`;
        switch (s.frequency) {
            case 'hourly':  return 'Every hour';
            case 'daily':   return `Every day at ${h}`;
            case 'weekly':  return `Every ${DAYS[s.dayOfWeek ?? 1]} at ${h}`;
            case 'monthly': return `Every month on the ${s.dayOfMonth ?? 1} at ${h}`;
            case 'yearly':  return `Every year on Jan 1 at ${h}`;
            default: return s.frequency;
        }
    };

    return (
        <Fragment>
            <CustomLoader show={loading} />

            <div className="sm">
                <div className="sm__header">
                    <div>
                        <h2 className="sm__title">Scheduled Emails</h2>
                        <p className="sm__sub">Automate recurring emails to groups or individuals.</p>
                    </div>
                    <button className="sm__add-btn" onClick={openCreate}>+ New Schedule</button>
                </div>

                {schedules.length === 0 && !loading ? (
                    <div className="sm__empty">
                        <div className="sm__empty-icon">🕐</div>
                        <p>No scheduled emails yet. Create one to get started.</p>
                    </div>
                ) : (
                    <div className="sm__list">
                        {schedules.map(s => (
                            <div className="sm__card" key={s._id}>
                                <div className="sm__card-top">
                                    <div className="sm__card-info">
                                        <div className="sm__card-title">{s.title}</div>
                                        <div className="sm__card-subject">Subject: {s.subject}</div>
                                    </div>
                                    <span className="sm__status" style={{ background: STATUS_COLORS[s.status] + '20', color: STATUS_COLORS[s.status] }}>
                                        {s.status}
                                    </span>
                                </div>

                                <div className="sm__card-meta">
                                    <span>🔁 {describeSchedule(s)}</span>
                                    <span>
                                        {s.recipientType === 'group'
                                            ? `👥 ${s.mailGroupId?.name || 'Group'}`
                                            : `✉️ ${(s.emails || []).length} individual(s)`}
                                    </span>
                                </div>

                                <div className="sm__card-runs">
                                    <span>Last run: {fmt(s.lastRun)}</span>
                                    <span>Next run: {fmt(s.nextRun)}</span>
                                </div>

                                <div className="sm__card-actions">
                                    <button className="sm__btn sm__btn--edit" onClick={() => openEdit(s)}>✏️ Edit</button>
                                    {s.status === 'active'
                                        ? <button className="sm__btn sm__btn--pause" onClick={() => handleStatus(s._id, 'paused')}>⏸ Pause</button>
                                        : <button className="sm__btn sm__btn--resume" onClick={() => handleStatus(s._id, 'active')}>▶ Resume</button>
                                    }
                                    <button className="sm__btn sm__btn--delete" onClick={() => setConfirmDel(s)}>🗑️</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showForm && (
                <ScheduleFormModal
                    title={editItem ? 'Edit Schedule' : 'New Scheduled Email'}
                    form={form}
                    onChange={handleChange}
                    onSubmit={handleSubmit}
                    onClose={() => setShowForm(false)}
                    groups={groups}
                />
            )}

            {confirmDel && (
                <div className="sm-modal__backdrop" onClick={() => setConfirmDel(null)}>
                    <div className="sm-modal sm-modal--sm" onClick={e => e.stopPropagation()}>
                        <div className="sm-modal__header">
                            <h3 className="sm-modal__title">Delete Schedule</h3>
                            <button className="sm-modal__close" onClick={() => setConfirmDel(null)}>✕</button>
                        </div>
                        <div className="sm-modal__body">
                            <p>Delete <strong>{confirmDel.title}</strong>? This cannot be undone.</p>
                        </div>
                        <div className="sm-modal__footer">
                            <button className="sm-modal__btn sm-modal__btn--cancel" onClick={() => setConfirmDel(null)}>Cancel</button>
                            <button className="sm-modal__btn sm-modal__btn--delete" onClick={() => handleDelete(confirmDel._id)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {toast && (
                <div className={`sm__toast sm__toast--${toast.ok ? 'ok' : 'err'}`}>
                    {toast.msg}
                </div>
            )}
        </Fragment>
    );
};

export default ScheduledMail;
